import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Button,
  Form, 
  InputGroup, 
  Badge, 
  Spinner,
  Dropdown
} from 'react-bootstrap';
import GenericButton from '../updated/shared/Button';
import { 
  getOffers, 
  type Offer 
} from '../../services/p2pAPI';
import './OffersBrowse.css';
import IconicHeader from '../updated/shared/IconicHeader';
import P2PNavigation from './P2PNavigation';

const OffersBrowse: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set background color IMMEDIATELY on mount (before any rendering)
  useLayoutEffect(() => {
    const body = document.body;
    const root = document.documentElement;
    try {
      const bgColor = getComputedStyle(root).getPropertyValue('--main-body').trim() || '#0a0e27';
      body.style.setProperty('background-color', bgColor, 'important');
      root.style.setProperty('background-color', bgColor, 'important');
    } catch (e) {
      body.style.setProperty('background-color', '#0a0e27', 'important');
      root.style.setProperty('background-color', '#0a0e27', 'important');
    }
  }, []);
  
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    cryptoType: '',
    paymentMethod: '',
    type: '' as '' | 'buy' | 'sell',
    minPrice: '',
    maxPrice: '',
    sortBy: 'price' as 'price' | 'volume' | 'completionRate',
    sortOrder: 'asc' as 'asc' | 'desc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadOffers();
  }, [filters, pagination.page]);

  // Apply theme class based on userType (CaptainBee = orange, normal = green)
  // Match the logic used in IconicHeader: if userType !== 'Indexx Exchange', it's CaptainBee
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    
    // Determine theme class based on userType or userlogged
    // Match IconicHeader logic: userType !== 'Indexx Exchange' means CaptainBee
    let themeClass = 'theme-normal'; // Default to green
    
    if (userType && userType !== 'Indexx Exchange') {
      // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Orange for HoneyBee
    } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
    }
    
    // Apply theme class to document body and root
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    // Also apply to root element to ensure CSS variables are available
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    // Cleanup on unmount
    return () => {
      // Don't remove theme class on unmount as other components might need it
    };
  }, []);

  // Refresh offers when page becomes visible or user navigates back
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, refreshing offers...');
        loadOffers();
      }
    };

    const handleFocus = () => {
      console.log('Window focused, refreshing offers...');
      loadOffers();
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for window focus (when user navigates back)
    window.addEventListener('focus', handleFocus);

    // Check if a trade was created recently (stored in localStorage)
    const checkTradeCreated = () => {
      const lastTradeCreated = localStorage.getItem('p2p_trade_created');
      if (lastTradeCreated) {
        const tradeTime = parseInt(lastTradeCreated, 10);
        const now = Date.now();
        // If trade was created within last 30 seconds, refresh
        if (now - tradeTime < 30000) {
          console.log('Trade was recently created, refreshing offers...');
          loadOffers();
          localStorage.removeItem('p2p_trade_created');
        }
      }
    };

    // Check on mount
    checkTradeCreated();

    // Also check when location changes (user navigates back)
    if (location.key) {
      checkTradeCreated();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.key]); // Re-run when location changes

  // Helper function to format payment method names
  const formatPaymentMethod = (method: string): string => {
    if (!method) return 'Bank Transfer';
    const methodUpper = method.toUpperCase().trim();
    
    // Asset Wallet cases
    if (methodUpper === 'ASSET WALLET' || 
        methodUpper === 'ASSET_WALLET' ||
        methodUpper === 'INTERNALUSD' || 
        methodUpper === 'INTERNAL_USD' || 
        methodUpper === 'INTERNAL' ||
        methodUpper.includes('INTERNALUSD') || 
        methodUpper.includes('INTERNAL_USD') ||
        methodUpper.includes('ASSET')) {
      return 'Asset Wallet';
    }
    
    // Real Fiat / Gateway cases - format them nicely
    if (methodUpper === 'REAL_FIAT' || methodUpper === 'REAL FIAT') {
      return 'Gateway';
    }
    if (methodUpper === 'GATEWAY') {
      return 'Gateway';
    }
    if (methodUpper === 'BANK_TRANSFER' || methodUpper === 'BANK TRANSFER') {
      return 'Bank Transfer';
    }
    if (methodUpper === 'FIAT') {
      return 'Fiat';
    }
    
    // Return the method as-is if it doesn't match any special cases
    return method;
  };

  // Helper to navigate to offer details using React Router (no page reload = no white flash)
  const navigateToOffer = (offerId: string) => {
    // Ensure background is set IMMEDIATELY and SYNCHRONOUSLY before navigation
    const body = document.body;
    const root = document.documentElement;
    try {
      const bgColor = getComputedStyle(root).getPropertyValue('--main-body').trim() || '#0a0e27';
      body.style.setProperty('background-color', bgColor, 'important');
      root.style.setProperty('background-color', bgColor, 'important');
      // Force synchronous style application
      void body.offsetHeight; // Trigger reflow to force style application
    } catch (e) {
      body.style.setProperty('background-color', '#0a0e27', 'important');
      root.style.setProperty('background-color', '#0a0e27', 'important');
    }
    
    // Navigate immediately - React Router handles the transition
    navigate(`/p2p/offers/${offerId}`);
  };

  const loadOffers = async () => {
    try {
      setLoading(true);
      console.log('Loading offers with filters:', filters);
      // Use a high limit to fetch all offers, or use pagination limit if filtering
      // When fetching all offers (no type filter), we need to get all offers from both buy and sell endpoints
      const requestLimit = filters.type ? pagination.limit : 1000; // High limit for "all offers" to get all results
      const response = await getOffers({
        ...filters,
        minPrice: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
        maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
        page: filters.type ? pagination.page : 1, // Only paginate if filtering by type
        limit: requestLimit,
      });
      
      // Normalize offers to ensure type is 'buy' | 'sell'
      const rawOffers: any[] = Array.isArray(response.data) ? response.data : [];
      
      const offersData = rawOffers.map((offer: any) => {
        // Safely normalize the type field
        let normalizedType: 'buy' | 'sell' = 'buy';
        if (offer.type) {
          const typeStr = String(offer.type).toLowerCase().trim();
          normalizedType = typeStr === 'sell' ? 'sell' : 'buy';
        }
        
        return {
          ...offer,
          type: normalizedType
        };
      });
      
      setOffers(offersData);
      console.log('Offers received in component:', offersData.length);
      console.log('Offers breakdown:', offersData.map(o => ({ id: o.id, type: o.type, crypto: o.cryptoType })));
      console.log('Buy offers count:', offersData.filter(o => o.type === 'buy').length);
      console.log('Sell offers count:', offersData.filter(o => o.type === 'sell').length);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 1,
      }));
    } catch (error) {
      console.error('Failed to load offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    console.log('Filter change:', key, 'from', filters[key as keyof typeof filters], 'to', value);
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      cryptoType: '',
      paymentMethod: '',
      type: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'price',
      sortOrder: 'asc',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getCryptoIcon = (cryptoType: string) => {
    const icons: { [key: string]: string } = {
      'BTC': 'fab fa-bitcoin',
      'ETH': 'fab fa-ethereum',
      'BNB': 'fas fa-coins',
      'USDT': 'fas fa-dollar-sign',
      'USDC': 'fas fa-dollar-sign',
      'BUSD': 'fas fa-dollar-sign',
    };
    return icons[cryptoType] || 'fas fa-coins';
  };

  // Helper function to get token logo
  const getTokenLogo = (coinSymbol: string) => {
    try {
      // Normalize the symbol
      const normalizedSymbol = coinSymbol.toUpperCase().trim();
      
      // Handle special cases and variations
      const symbolMap: { [key: string]: string } = {
        'USD': 'USD',
        'USDT': 'USDT',
        'USDC': 'USDC',
        'BTC': 'BTC',
        'ETH': 'ETH',
        'BNB': 'BNB',
        'BUSD': 'BUSD',
        'INEX': 'INEX',
        'IN500': 'IN500',
        'INXC': 'INXC',
        'IUSD': 'IUSD',
        'IUSD+': 'IUSD+',
        'IUSDP': 'IUSDP',
        'ADA': 'ADA',
        'DOT': 'DOT',
        'LINK': 'LINK',
        'MATIC': 'MATIC',
        'SOL': 'SOL',
        'XRP': 'XRP',
        'DOGE': 'DOGE',
        'AVAX': 'AVAX',
        'LTC': 'LTC',
        'CAKE': 'CAKE',
        'AAVE': 'AAVE',
        'SHIB': 'SHIB',
        'TRX': 'TRX',
        'VET': 'VET',
        'CHZ': 'CHZ',
        'MANA': 'MANA',
        'THETA': 'THETA',
        'FTM': 'FTM',
        'ALGO': 'ALCRYP',
        'BCH': 'BCH',
        'XLM': 'XLM',
        'TUSD': 'TUSD',
        'DAI': 'DAI',
        'IBAT': 'IBAT',
        'EPS': 'EPS',
        'LBLOCK': 'LBlock',
      };

      // Get the image name from map or use the symbol directly
      let imageName = symbolMap[normalizedSymbol] || normalizedSymbol;
      
      // Handle special symbols with special characters
      if (normalizedSymbol.includes('+') || normalizedSymbol.includes('-')) {
        imageName = normalizedSymbol.replace(/[+-\s]/g, '');
      }
      
      try {
        // Try loading the image
        return require(`../../assets/token-icons/${imageName}.png`).default;
      } catch (error1) {
        // If that fails, try with the original normalized symbol
        try {
          return require(`../../assets/token-icons/${normalizedSymbol}.png`).default;
        } catch (error2) {
          return null;
        }
      }
    } catch (error) {
      return null;
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    const icons: { [key: string]: string } = {
      'Bank Transfer': 'fas fa-university',
      'PayPal': 'fab fa-paypal',
      'Wise': 'fas fa-exchange-alt',
      'Cash App': 'fas fa-mobile-alt',
      'Zelle': 'fas fa-mobile-alt',
      'Venmo': 'fas fa-mobile-alt',
    };
    return icons[method] || 'fas fa-credit-card';
  };

  const cryptoTypes = ['BTC', 'ETH', 'BNB', 'USDT', 'USDC', 'BUSD'];
  const paymentMethods = ['Bank Transfer', 'PayPal', 'Wise', 'Cash App', 'Zelle', 'Venmo'];

  return (
    <div className="binance-p2p-container">
      <IconicHeader selectedTab="P2P" onChange={() => {}} />
      <P2PNavigation />
      {/* Removed page title header; IconicHeader is used globally on P2P */}
      
      <div className="p2p-content-container">
      {/* Binance P2P Filter Section */}
      <div className="binance-filter-section">
        <div className="filter-container">
          {/* Filters Row */}
          <div className="filters-row">
            <div className="filter-item">
              <label>Type</label>
              <Dropdown>
                <Dropdown.Toggle className="binance-select" id="dropdown-type" variant="secondary">
                  {filters.type === '' ? 'All' : filters.type === 'buy' ? 'BUY' : 'Sell'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleFilterChange('type', '')}>All</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleFilterChange('type', 'buy')}>BUY</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleFilterChange('type', 'sell')}>Sell</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="filter-item">
              <label>Coin</label>
              <Dropdown>
                <Dropdown.Toggle className="binance-select" id="dropdown-coin" variant="secondary">
                  {filters.cryptoType === '' ? 'All Coins' : filters.cryptoType}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleFilterChange('cryptoType', '')}>All Coins</Dropdown.Item>
                {cryptoTypes.map(crypto => (
                    <Dropdown.Item key={crypto} onClick={() => handleFilterChange('cryptoType', crypto)}>{crypto}</Dropdown.Item>
                ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="filter-item">
              <label>Payment</label>
              <Dropdown>
                <Dropdown.Toggle className="binance-select" id="dropdown-payment" variant="secondary">
                  {filters.paymentMethod === '' ? 'All Payments' : formatPaymentMethod(filters.paymentMethod)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleFilterChange('paymentMethod', '')}>All Payments</Dropdown.Item>
                {paymentMethods.map(method => (
                    <Dropdown.Item key={method} onClick={() => handleFilterChange('paymentMethod', method)}>{formatPaymentMethod(method)}</Dropdown.Item>
                ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="filter-item">
              <label>Price Range</label>
              <div className="price-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="binance-input"
                />
                <span className="price-separator">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="binance-input"
                />
              </div>
            </div>

            <div className="filter-item">
              <label>Sort by</label>
              <Dropdown>
                <Dropdown.Toggle className="binance-select" id="dropdown-sort" variant="secondary">
                  {(() => {
                    const v = `${filters.sortBy}-${filters.sortOrder}`;
                    if (v === 'price-asc') return 'Price: Low to High';
                    if (v === 'price-desc') return 'Price: High to Low';
                    if (v === 'volume-desc') return 'Volume: High to Low';
                    if (v === 'completionRate-desc') return 'Rating: High to Low';
                    return 'Sort';
                  })()}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { handleFilterChange('sortBy', 'price'); handleFilterChange('sortOrder', 'asc'); }}>Price: Low to High</Dropdown.Item>
                  <Dropdown.Item onClick={() => { handleFilterChange('sortBy', 'price'); handleFilterChange('sortOrder', 'desc'); }}>Price: High to Low</Dropdown.Item>
                  <Dropdown.Item onClick={() => { handleFilterChange('sortBy', 'volume'); handleFilterChange('sortOrder', 'desc'); }}>Volume: High to Low</Dropdown.Item>
                  <Dropdown.Item onClick={() => { handleFilterChange('sortBy', 'completionRate'); handleFilterChange('sortOrder', 'desc'); }}>Rating: High to Low</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="filter-item">
              <GenericButton
                text="Reset"
                onClick={clearFilters}
                IconComponent={() => null}
                className=""
                styles={{}}
                disabled={false}
                loading={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="offers-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading offers...</p>
        </div>
      ) : (
        <>
          <div className="offers-results-header">
            <span className="debug-info" style={{fontSize: '12px', color: '#666', marginLeft: '10px'}}>
              Filter: {filters.type || 'all'} | Showing: {offers.length} offers | Buy: {offers.filter(o => o.type === 'buy').length} | Sell: {offers.filter(o => o.type === 'sell').length}
            </span>
          </div>

          {offers.length > 0 && (
            <>
              {/* Binance P2P Results Section */}
              <div className="binance-results-section">
                <div className="results-header">
                  <div className="results-info">
                    <span className="results-type">
                      {filters.type === 'buy' ? 'Buy' : filters.type === 'sell' ? 'Sell' : 'All'} {filters.cryptoType || 'Coins'}
                    </span>
                    <span className="debug-info" style={{fontSize: '12px', color: '#666', marginLeft: '10px'}}>
                      Filter: {filters.type || 'all'} | Showing: {offers.length} offers | Buy: {offers.filter(o => o.type === 'buy').length} | Sell: {offers.filter(o => o.type === 'sell').length}
                    </span>
                  </div>
                </div>

                {/* Binance P2P Table Style */}
                <div className="binance-offers-table">
                  {/* Table Header */}
                  <div className="offers-table-header">
                    <div className="col-trader">Trader</div>
                    <div className="col-price">Price</div>
                    <div className="col-available">Available</div>
                    <div className="col-limits">Limits</div>
                    <div className="col-token-out">Token Out</div>
                    <div className="col-payment">Payment Methods</div>
                    <div className="col-action">Action</div>
                  </div>
                  
                  {/* Table Body */}
                  <div className="offers-table-body">
                    {offers.map((offer) => (
                      <div key={offer.id} className="offer-table-row" onClick={() => navigateToOffer(offer.id)}>
                        {/* Trader Column */}
                        <div className="col-trader">
                          <div className="trader-avatar">
                            <i className="fas fa-user-circle"></i>
                          </div>
                          <div className="trader-info">
                            <div className="trader-name">{offer.username}</div>
                            <div className="trader-stats">
                              <span className="trade-orders">{offer.totalTrades} orders</span>
                              <span className="separator">|</span>
                              <span className="completion-rate">{offer.completionRate}% completion</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Price Column */}
                        <div className="col-price">
                          <div className="price-amount">{formatCurrency(offer.price)}</div>
                        </div>
                        
                        {/* Available Column */}
                        <div className="col-available">
                          <div className="available-value">{offer.amount} {offer.cryptoType}</div>
                        </div>
                        
                        {/* Limits Column */}
                        <div className="col-limits">
                          <div className="limits-text">{formatCurrency(offer.minAmount)} - {formatCurrency(offer.maxAmount)}</div>
                        </div>
                        
                        {/* Token Out Column */}
                        <div className="col-token-out">
                          <div className="token-out-value">
                            {offer.returnToken ? (
                              <>
                                {(() => {
                                  const tokenLogo = getTokenLogo(offer.returnToken);
                                  return (
                                    tokenLogo ? (
                                      <img 
                                        src={tokenLogo} 
                                        alt={offer.returnToken}
                                        className="token-out-icon-image"
                                        style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '8px' }}
                                      />
                                    ) : (
                                      <i className={`${getCryptoIcon(offer.returnToken)} me-2`}></i>
                                    )
                                  );
                                })()}
                                {offer.returnAmount && (
                                  <span className="token-out-amount"> ({offer.returnAmount})</span>
                                )}
                              </>
                            ) : (
                              <span className="token-out-empty">-</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Payment Methods Column */}
                        <div className="col-payment">
                          <div className="payment-methods-list">
                            {offer.paymentMethods && offer.paymentMethods.length > 0 ? (
                              <>
                                <span className="payment-method payment-method-primary">
                                  {formatPaymentMethod(offer.paymentMethods[0])}
                                  {offer.paymentMethods.length > 1 && (
                                    <span className="payment-method-count"> +{offer.paymentMethods.length - 1}</span>
                                  )}
                                </span>
                                {offer.paymentMethods.length > 1 && (
                                  <div className="payment-methods-tooltip">
                                    {offer.paymentMethods.map((method: string, idx: number) => (
                                      <div key={idx} className="tooltip-method">{formatPaymentMethod(method)}</div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="payment-method">{formatPaymentMethod(offer.paymentMethod || 'Bank Transfer')}</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Column */}
                        <div className="col-action">
                          <div className={`filter-toggle-btn ${offer.type === 'buy' ? 'buy-filter-btn' : 'sell-filter-btn'}`}>
                            <GenericButton
                              text={`${offer.type === 'buy' ? 'Buy' : 'Sell'} ${offer.cryptoType}`}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                navigateToOffer(offer.id);
                              }}
                              IconComponent={() => null}
                              className=""
                              styles={{}}
                              disabled={false}
                              loading={false}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pagination removed in favor of vertical scroll */}
            </>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default OffersBrowse;
